import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, Dumbbell, Heart } from 'lucide-react';

const features = [
  {
    id: 1,
    icon: Dumbbell,
    title: 'Fighter Level Training',
    description: 'Train with the same intensity and techniques used by professional MMA fighters and champions.',
    stat: '100%',
    statLabel: 'Fight Ready',
  },
  {
    id: 2,
    icon: Users,
    title: 'Strength & Conditioning',
    description: 'Comprehensive fitness programs designed to build functional strength, endurance, and explosive power.',
    stat: '7',
    statLabel: 'Programs',
  },
  {
    id: 3,
    icon: Heart,
    title: 'Supportive Community',
    description: 'Join a family of passionate martial artists who support and motivate each other to reach new heights.',
    stat: '500+',
    statLabel: 'Members',
  },
];

const WhyUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="why-us"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#050505] overflow-hidden"
      data-testid="why-us-section"
    >
      {/* Background accent */}
      <div className="absolute top-1/2 -translate-y-1/2 -left-64 w-[500px] h-[500px] bg-[#DC2626]/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-[#DC2626] text-sm font-bold tracking-[0.3em] uppercase">
            Why Choose Us
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Train With<br />
            <span className="text-[#DC2626]">The Best</span>
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            What sets SDM apart from other gyms? Our commitment to excellence in every aspect of training.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-[#0A0A0A] border border-[#262626] p-8 hover:border-[#DC2626]/50 transition-all duration-300"
              data-testid={`feature-card-${feature.id}`}
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#DC2626] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center bg-[#121212] border border-[#262626] mb-6 group-hover:border-[#DC2626] transition-colors">
                <feature.icon size={28} className="text-[#DC2626]" />
              </div>

              {/* Title */}
              <h3 className="font-['Oswald'] text-xl text-white font-bold uppercase tracking-wide mb-4">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-neutral-400 text-sm leading-relaxed mb-6">
                {feature.description}
              </p>

              {/* Stat */}
              <div className="pt-6 border-t border-[#262626]">
                <span className="font-['Oswald'] text-3xl font-bold text-[#DC2626]">
                  {feature.stat}
                </span>
                <p className="text-neutral-500 text-xs uppercase tracking-wider mt-1">
                  {feature.statLabel}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-neutral-400 mb-6">
            Ready to start your martial arts journey with us?
          </p>
          <a
            href="#contact"
            className="inline-block btn-skew bg-[#DC2626] text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#B91C1C] transition-all"
            data-testid="why-us-cta-btn"
          >
            <span>Start Training Today</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUsSection;
